/**
 * useNotebookFileActions (Intelligence Studio)
 *
 * SRP hook: file operations inside a notebook (upload/retry/remove/delete/rename/reupload + refresh + selection).
 * Extracted (copy/paste) from containers/IntelligenceStudioPage.jsx.
 */

export default function useNotebookFileActions({
  intelligenceStudioApi,
  uploadLockRef,
  selectedNotebook,
  setSelectedNotebook,
  setNotebooks,
  setError,
  uploadFileForNanoMetadata,
  removeNanoMetadataRow,
  rightCollapsed,
  expandRight,
  selectedFile,
  setSelectedFile,
}) {
  const normalizeName = name => (name || '').trim().toLowerCase();
  const getSize = f => Number(f?.size || f?.fileSize || 0) || 0;

  const refreshNotebook = async () => {
    if (!selectedNotebook) return;
    try {
      const response = await intelligenceStudioApi.getNotebook(selectedNotebook.id);
      const serverNotebook = response?.data?.data || response?.data || response;
      const serverFiles = serverNotebook?.files || [];

      // ✅ ENTERPRISE FIX: MERGE server files with local-only files (preserve error/uploading states)
      // CRITICAL: Failed files (status === 'error') MUST be preserved so users can retry or delete
      setSelectedNotebook(prev => {
        const localOnlyFiles = (prev?.files || []).filter(f => {
          const isLocalOnly =
            f.id?.startsWith('temp_') ||
            f.status === 'uploading' ||
            f.status === 'error' || // ✅ PRESERVE ERROR FILES
            f.status === 'failed' || // ✅ PRESERVE FAILED FILES
            f.status === 'processing';
          const notOnServer = !serverFiles.some(sf => {
            // Match by ID, fileId, or originalName (for failed files that might not have server ID yet)
            const sameName =
              normalizeName(sf.originalName || sf.name || sf.filename) ===
              normalizeName(f.originalName || f.name || f.filename);
            const sameSize = getSize(sf) > 0 && getSize(f) > 0 && getSize(sf) === getSize(f);
            return (
              sf.id === f.id ||
              sf.fileId === f.id ||
              sf.fileId === f.fileId ||
              // ✅ ENTERPRISE FIX: Collapse upload placeholders once the real server file exists
              // This prevents “uploaded once but appears twice” (temp_ row + real row).
              ((f.id?.startsWith('temp_') ||
                f.status === 'uploading' ||
                f.status === 'processing') &&
                sameName &&
                (sameSize || getSize(f) === 0 || getSize(sf) === 0)) ||
              (sf.originalName === f.originalName && f.status === 'error')
            ); // ✅ Match failed files by name
          });
          return isLocalOnly && notOnServer;
        });

        return {
          ...serverNotebook,
          files: [...serverFiles, ...localOnlyFiles],
        };
      });

      setNotebooks(prev => prev.map(n => (n.id === serverNotebook.id ? serverNotebook : n)));
    } catch (err) {
      console.error('Failed to refresh:', err);
    }
  };

  // Handle file upload to current notebook
  const handleUploadFile = async (notebookId, file) => {
    // ✅ ENTERPRISE: Upload lock to prevent double-uploads
    const fileKey = `${notebookId}:${file.name}:${file.size}`;
    if (uploadLockRef.current.has(fileKey)) {
      console.log(`🔒 [Upload] SKIPPED "${file.name}" - already uploading`);
      return;
    }
    uploadLockRef.current.add(fileKey);

    // ✅ ENTERPRISE UX: Add placeholder file immediately with 'checking' status
    const tempId = `temp_${Date.now()}`;
    const placeholderFile = {
      id: tempId,
      fileId: tempId,
      originalName: file.name,
      name: file.name,
      size: file.size,
      status: 'checking', // ✅ Start with checking status
      progress: 10,
      originalFile: file,
    };

    // Add to UI immediately
    setSelectedNotebook(prev => ({
      ...prev,
      files: [...(prev?.files || []), placeholderFile],
    }));

    // ✅ STEP 1: Smart Pre-Check (hash-based)
    try {
      const preCheck = await intelligenceStudioApi.checkFileBeforeUpload(notebookId, file);

      if (!preCheck.shouldUpload) {
        // ✅ ENTERPRISE UX: Mark as duplicate but KEEP visible (don't auto-remove)
        // User needs to see what happened to their selected file
        console.log(
          `⚠️ [SmartUpload] "${file.name}" already exists in vault - marking as duplicate`
        );
        uploadLockRef.current.delete(fileKey);

        // ✅ KEEP the file visible with duplicate status
        setSelectedNotebook(prev => ({
          ...prev,
          files: (prev?.files || []).map(f =>
            f.id === tempId
              ? {
                  ...f,
                  status: 'duplicate',
                  statusMessage: '📎 Already in vault (hash match)',
                  existingFileId: preCheck.existingFileId,
                  cachedQuality: preCheck.cachedQuality,
                  progress: 100,
                  error: null, // Not an error - just info
                }
              : f
          ),
        }));

        // If we know the existing file ID, highlight it (but don't remove current)
        if (preCheck.existingFileId) {
          setSelectedNotebook(prev => {
            const existing = (prev?.files || []).find(
              f => (f.fileId || f.id) === preCheck.existingFileId
            );
            if (existing) setSelectedFile(existing);
            return prev;
          });
        }

        // Show info message (not error)
        setError(`ℹ️ "${file.name}" content already exists in vault (duplicate detected).`);
        return;
      }

      // Update status based on cache
      const statusMessage =
        preCheck.action === 'UPLOAD_FAST' ? '⚡ Using cache...' : 'Uploading...';

      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f =>
          f.id === tempId ? { ...f, status: 'uploading', progress: 30, statusMessage } : f
        ),
      }));
    } catch (checkErr) {
      console.warn(`⚠️ [SmartUpload] Pre-check failed, proceeding:`, checkErr.message);
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f =>
          f.id === tempId ? { ...f, status: 'uploading', progress: 30 } : f
        ),
      }));
    }

    // ✅ NANO-METADATA ENGINE: Phase-1 instant row (NO server enhancement in Intelligence Studio)
    // Use the same tempId so SmartTable can merge/upgrade seamlessly once server fileId exists.
    uploadFileForNanoMetadata(file, tempId);

    // ✅ STEP 2: Actual Upload
    try {
      // Update to show upload in progress
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f => (f.id === tempId ? { ...f, progress: 70 } : f)),
      }));

      const uploadResult = await intelligenceStudioApi.uploadFile(notebookId, file);

      // ✅ Check for cache usage (file was deduplicated)
      if (uploadResult?.data?.usedCache) {
        console.log('📦 [Upload] File used cached OCR from previous upload');
      }

      // Update to show processing
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f =>
          f.id === tempId ? { ...f, status: 'processing', progress: 100 } : f
        ),
      }));

      // Refresh notebook to get actual file data from server
      const updated = await intelligenceStudioApi.getNotebook(notebookId);

      // ✅ FIX: Robust parsing - apiClient returns {success, data}
      let serverNotebook = null;
      if (updated?.data?.id) {
        serverNotebook = updated.data;
      } else if (updated?.id) {
        serverNotebook = updated;
      }

      if (serverNotebook) {
        const serverFiles = serverNotebook.files || [];
        console.log(`✅ [Upload] Refresh complete. Server files: ${serverFiles.length}`);

        // ✅ ENTERPRISE FIX: MERGE server files with local-only files (error, uploading, temp)
        // CRITICAL: Failed files (status === 'error') MUST be preserved so users can retry or delete
        setSelectedNotebook(prev => {
          const localOnlyFiles = (prev?.files || []).filter(f => {
            const isLocalOnly =
              f.id?.startsWith('temp_') ||
              f.status === 'uploading' ||
              f.status === 'error' || // ✅ PRESERVE ERROR FILES
              f.status === 'failed' || // ✅ PRESERVE FAILED FILES
              f.status === 'processing';
            const notOnServer = !serverFiles.some(sf => {
              // Match by ID, fileId, or originalName (for failed files that might not have server ID yet)
              const sameName =
                normalizeName(sf.originalName || sf.name || sf.filename) ===
                normalizeName(f.originalName || f.name || f.filename);
              const sameSize = getSize(sf) > 0 && getSize(f) > 0 && getSize(sf) === getSize(f);
              return (
                sf.id === f.id ||
                sf.fileId === f.id ||
                sf.fileId === f.fileId ||
                // ✅ ENTERPRISE FIX: Collapse upload placeholders once the real server file exists
                ((f.id?.startsWith('temp_') ||
                  f.status === 'uploading' ||
                  f.status === 'processing') &&
                  sameName &&
                  (sameSize || getSize(f) === 0 || getSize(sf) === 0)) ||
                (sf.originalName === f.originalName && f.status === 'error')
              ); // ✅ Match failed files by name
            });
            return isLocalOnly && notOnServer;
          });

          return {
            ...serverNotebook,
            files: [...serverFiles, ...localOnlyFiles],
          };
        });

        setNotebooks(prev => prev.map(n => (n.id === serverNotebook.id ? serverNotebook : n)));
      }

      // ✅ Release upload lock on success
      uploadLockRef.current.delete(fileKey);
    } catch (err) {
      // ✅ Release upload lock on error
      uploadLockRef.current.delete(fileKey);

      // ✅ ENTERPRISE FIX: Mark file as error and PRESERVE it (don't remove)
      console.error(`❌ [Upload] File upload failed:`, err);

      // Extract error message
      const errorData = err?.response?.data || null;
      const errorCode =
        errorData?.code || errorData?.reason || errorData?.status || errorData?.errorCode || null;
      const errorMessage =
        errorData?.message ||
        errorData?.error ||
        errorData?.details?.error ||
        err?.message ||
        'Upload failed';
      const isWorkerUnavailable =
        errorData?.code === 'WORKERS_UNAVAILABLE' || err.response?.status === 503;
      const isUnsupportedType =
        errorData?.code === 'UNSUPPORTED_FILE_TYPE' || err.response?.status === 415;

      // ✅ Handle duplicate file error specially
      const isDuplicateError = errorData?.code === 'DUPLICATE_FILE' || err.response?.status === 409;

      if (isDuplicateError) {
        // ✅ ENTERPRISE FIX: Mark as duplicate but DON'T remove - let user decide
        // User might want to see that a duplicate was attempted, and delete the existing one
        setSelectedNotebook(prev => {
          const updatedFiles = (prev?.files || []).map(f =>
            f.id === tempId
              ? {
                  ...f,
                  status: 'duplicate',
                  error:
                    'This file already exists in this vault. Delete the existing one to re-upload.',
                  existingFileId: errorData?.existingFileId,
                  cachedQuality: errorData?.cachedQuality, // ✅ Show quality of existing
                  progress: 0,
                }
              : f
          );

          return {
            ...prev,
            files: updatedFiles,
          };
        });

        // ✅ Show helpful message with quality info if available
        const qualityInfo = errorData?.cachedQuality
          ? ` (Current quality: ${errorData.cachedQuality}/100)`
          : '';
        setError(
          `⚠️ This exact file already exists.${qualityInfo} You can delete the existing one to re-upload.`
        );
      } else {
        // ✅ ENTERPRISE FIX: Mark file as error and persist to localStorage
        setSelectedNotebook(prev => {
          const updatedFiles = (prev?.files || []).map(f =>
            f.id === tempId
              ? {
                  ...f,
                  status: isWorkerUnavailable || isUnsupportedType ? 'failed' : 'error',
                  error: errorCode ? `${errorCode}` : errorMessage,
                  statusMessage: isWorkerUnavailable
                    ? 'Workers offline - ingestion paused'
                    : isUnsupportedType
                      ? 'Unsupported file type'
                      : undefined,
                  progress: 0,
                  originalFile: f.originalFile || file, // ✅ Store for retry
                }
              : f
          );

          // ✅ Persist failed files to localStorage (survives page refresh)
          const failedFiles = updatedFiles.filter(
            f => f.status === 'error' || f.status === 'failed' || f.status === 'duplicate'
          );
          if (failedFiles.length > 0 && prev.id) {
            try {
              const storageKey = `failed_files_${prev.id}`;
              const serializableFailedFiles = failedFiles.map(f => ({
                id: f.id,
                fileId: f.fileId,
                originalName: f.originalName || f.name,
                name: f.name,
                size: f.size,
                status: f.status,
                error: f.error,
              }));
              localStorage.setItem(storageKey, JSON.stringify(serializableFailedFiles));
              console.log(
                `💾 [Upload] Persisted ${failedFiles.length} failed files to localStorage`
              );
            } catch (storageErr) {
              console.warn('Failed to persist failed files to localStorage:', storageErr);
            }
          }

          return {
            ...prev,
            files: updatedFiles,
          };
        });
        setError(
          isWorkerUnavailable
            ? 'Workers are offline. Ingestion is temporarily unavailable.'
            : isUnsupportedType
              ? 'Unsupported file type. Please upload a supported document.'
              : errorMessage || 'Failed to upload file'
        );
      }
    }
  };

  // Handle file selection for preview
  const handleFileSelect = file => {
    setSelectedFile(file);
    // Expand right panel if collapsed
    if (rightCollapsed) expandRight();
  };

  // ✅ Handle remove duplicates
  const handleRemoveDuplicates = async notebookId => {
    if (!notebookId) return;
    try {
      // ✅ ENTERPRISE: Optimistically clear hash-duplicates from UI immediately
      setSelectedNotebook(prev => {
        const files = prev?.files || [];
        const seen = new Set();
        const filtered = [];
        for (const f of files) {
          const h = f.fileHash || f.hash || null;
          if (!h) {
            filtered.push(f);
            continue;
          }
          if (seen.has(h)) continue;
          seen.add(h);
          filtered.push(f);
        }
        return { ...prev, files: filtered };
      });

      const response = await intelligenceStudioApi.removeDuplicates(notebookId);
      const result = response?.data?.data || response?.data || response;

      if (result.removed > 0) {
        // Refresh notebook to get updated file list
        await refreshNotebook();
        setError(null);
        console.log(`✅ Removed ${result.removed} duplicate files`);
      } else {
        console.log('No duplicates found');
      }
    } catch (err) {
      console.error('Failed to remove duplicates:', err);
      setError('Failed to remove duplicates');
    }
  };

  // ✅ Handle retry failed file upload
  const handleRetryFile = async file => {
    if (!selectedNotebook?.id || !file) return;

    const fileName = file.originalName || file.name;
    const fileId = file.fileId || file.id;
    console.log(`🔄 [Retry] Retry requested for: ${fileName} (id: ${fileId})`);

    // ✅ ENTERPRISE FIX: Check if we have the original file BEFORE removing
    const originalFile = file.originalFile;
    if (!originalFile) {
      // Old vault / refreshed page: the browser no longer has the original File object.
      // Best-effort: retry processing on the server from the stored .bin copy (no re-upload).
      const isServerBacked = Boolean(fileId) && !String(fileId).startsWith('temp_');

      if (isServerBacked) {
        // Optimistic UI: mark as retrying
        setSelectedNotebook(prev => ({
          ...prev,
          files: (prev?.files || []).map(f =>
            (f.fileId || f.id) === fileId
              ? {
                  ...f,
                  status: 'processing',
                  processingStage: 'retry',
                  processingMessage: 'Retrying…',
                  progress: 0,
                  error: null,
                }
              : f
          ),
        }));

        try {
          const isLowOcrQuality = String(file?.error || '')
            .toUpperCase()
            .includes('LOW_OCR_QUALITY');
          await intelligenceStudioApi.reprocessFile(selectedNotebook.id, fileId, {
            reason: isLowOcrQuality
              ? 'Retry OCR: below minimum quality threshold'
              : 'Retry: previous processing failed (server reprocess)',
            tryCascade: true,
            qualityThreshold: isLowOcrQuality ? 85 : 50,
          });
          await refreshNotebook();
          setError(null);
          return;
        } catch (err) {
          const backendMsg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Retry failed';

          const needsReupload = String(backendMsg)
            .toLowerCase()
            .includes('original file not found on disk');

          if (needsReupload) {
            setError(
              `Source file missing for "${fileName}": this vault no longer has the original upload stored. Delete the file and upload a fresh copy if needed.`
            );
          } else {
            setError(`Retry failed for "${fileName}": ${backendMsg}`);
          }

          // Keep the file visible and actionable
          setSelectedNotebook(prev => ({
            ...prev,
            files: (prev?.files || []).map(f =>
              (f.fileId || f.id) === fileId
                ? {
                    ...f,
                    status: 'error',
                    error: needsReupload
                      ? 'Source file missing: original upload not available on server.'
                      : backendMsg,
                    requiresReupload: needsReupload || Boolean(f.requiresReupload),
                  }
                : f
            ),
          }));
          return;
        }
      }

      // Not server-backed (e.g. local placeholder after refresh): we can't retry without re-selecting.
      setError(
        `Source file missing for "${fileName}": your browser no longer has the original file. Delete it and upload again if needed.`
      );
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f =>
          (f.fileId || f.id) === fileId
            ? {
                ...f,
                status: 'error',
                error: 'Source file missing: delete and upload again if needed.',
                requiresReupload: true,
              }
            : f
        ),
      }));
      return;
    }

    // ✅ ENTERPRISE UX: Update to "retrying" status FIRST (don't remove yet)
    setSelectedNotebook(prev => ({
      ...prev,
      files: (prev?.files || []).map(f =>
        (f.fileId || f.id) === fileId
          ? {
              ...f,
              status: 'uploading',
              progress: 10,
              error: null,
              statusMessage: '🔄 Retrying...',
            }
          : f
      ),
    }));

    // ✅ Remove from localStorage
    const storageKey = `failed_files_${selectedNotebook.id}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const failedFiles = JSON.parse(stored);
        const updated = failedFiles.filter(f => (f.originalName || f.name) !== fileName);
        if (updated.length !== failedFiles.length) {
          localStorage.setItem(storageKey, JSON.stringify(updated));
          console.log(`🗑️ [Retry] Removed file from localStorage before retry`);
        }
      }
    } catch (err) {
      console.warn('Failed to update localStorage on retry:', err);
    }

    // ✅ Now remove the old entry and upload fresh
    // The handleUploadFile will create a new entry with temp_ id
    setSelectedNotebook(prev => ({
      ...prev,
      files: (prev?.files || []).filter(f => (f.fileId || f.id) !== fileId),
    }));

    // ✅ Also remove from SmartTable / nano-metadata rows
    if (removeNanoMetadataRow) {
      removeNanoMetadataRow(fileId);
    }

    // ✅ Now re-upload (will create a fresh entry)
    await handleUploadFile(selectedNotebook.id, originalFile);
  };

  // ✅ Handle remove failed file from list
  const handleRemoveFile = file => {
    if (!file) return;

    const fileId = file.fileId || file.id;
    const fileName = file.originalName || file.name;
    console.log(`🗑️ [Remove] Removing failed file: ${fileName}`);

    // ✅ ENTERPRISE FIX: Remove from localStorage as well
    if (selectedNotebook?.id) {
      try {
        const storageKey = `failed_files_${selectedNotebook.id}`;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const failedFiles = JSON.parse(stored);
          const updated = failedFiles.filter(f => (f.originalName || f.name) !== fileName);
          if (updated.length !== failedFiles.length) {
            localStorage.setItem(storageKey, JSON.stringify(updated));
            console.log(`🗑️ [Remove] Removed file from localStorage`);
          }
        }
      } catch (err) {
        console.warn('Failed to update localStorage on remove:', err);
      }
    }

    // Remove from local state
    setSelectedNotebook(prev => ({
      ...prev,
      files: (prev?.files || []).filter(f => (f.fileId || f.id) !== fileId),
    }));

    // Also remove from nano-metadata rows if present
    removeNanoMetadataRow(fileId);
  };

  // ✅ Handle delete file from vault (permanent)
  const handleDeleteFile = async file => {
    if (!selectedNotebook?.id || !file) return;

    const fileId = file.fileId || file.id;
    const fileName = file.originalName || file.name;

    // Confirm deletion
    if (
      !window.confirm(
        `Delete "${fileName}" permanently?\n\nThis will remove the file and all its processed data from the vault.`
      )
    ) {
      return;
    }

    console.log(`🗑️ [Delete] Permanently deleting: ${fileName}`);

    try {
      // Call API to delete file from server
      await intelligenceStudioApi.deleteFile(selectedNotebook.id, fileId);

      // Remove from local state
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).filter(f => (f.fileId || f.id) !== fileId),
      }));

      // Also remove from nano-metadata rows
      removeNanoMetadataRow(fileId);

      // Clear selection if this file was selected
      if (selectedFile?.id === fileId || selectedFile?.fileId === fileId) {
        setSelectedFile(null);
      }

      console.log(`✅ [Delete] Successfully deleted: ${fileName}`);
    } catch (err) {
      console.error('Failed to delete file:', err);
      setError(`Failed to delete "${fileName}"`);
    }
  };

  // ✅ Handle rename file
  const handleRenameFile = async file => {
    if (!selectedNotebook?.id || !file) return;

    const fileId = file.fileId || file.id;
    const currentName = file.originalName || file.name;

    // Prompt for new name
    const newName = window.prompt('Enter new file name:', currentName);
    if (!newName || newName === currentName) return;

    console.log(`✏️ [Rename] Renaming "${currentName}" to "${newName}"`);

    try {
      // Call API to rename file
      await intelligenceStudioApi.renameFile(selectedNotebook.id, fileId, newName);

      // Update local state
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f =>
          (f.fileId || f.id) === fileId ? { ...f, originalName: newName, name: newName } : f
        ),
      }));

      console.log(`✅ [Rename] Successfully renamed to: ${newName}`);
    } catch (err) {
      console.error('Failed to rename file:', err);
      // Check if it's a 404 (endpoint not implemented yet)
      if (err.message?.includes('404') || err.response?.status === 404) {
        setError('Rename feature coming soon!');
      } else {
        setError(`Failed to rename file`);
      }
    }
  };

  // ✅ Handle re-upload file (for incomplete chunks)
  const handleReuploadFile = async file => {
    if (!selectedNotebook?.id || !file) return;

    const fileName = file.originalName || file.name;
    const chunks = file.chunkCount || file.chunks || 0;
    const totalChunks = file.totalChunks || file.expectedChunks || chunks;

    console.log(`📤 [Re-upload] Re-uploading "${fileName}" (${chunks}/${totalChunks} chunks)`);

    // Check if we have the original file object stored
    let originalFile = file.originalFile;

    // If the browser File object is missing (old vault / after refresh), prompt the user to re-select it.
    if (!originalFile && typeof document !== 'undefined') {
      try {
        const picked = await new Promise(resolve => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = false;
          input.onchange = () => resolve(input.files?.[0] || null);
          input.click(); // must be triggered synchronously from a user gesture
        });

        if (!picked) {
          // User cancelled selection – do nothing
          return;
        }

        // Safety: confirm if they picked a different file name
        if (picked.name && fileName && picked.name !== fileName) {
          const ok = window.confirm(
            `You selected "${picked.name}".\n\nThis will replace "${fileName}" in this vault.\n\nContinue?`
          );
          if (!ok) return;
        }

        originalFile = picked;
      } catch (pickErr) {
        console.warn('File picker failed:', pickErr);
        setError(`Source file required for "${fileName}": please select the file again to upload.`);
        return;
      }
    }

    if (originalFile) {
      // Delete the existing file first
      try {
        const fileId = file.fileId || file.id;
        const isServerBacked = Boolean(fileId) && !String(fileId).startsWith('temp_');
        if (isServerBacked) {
          await intelligenceStudioApi.deleteFile(selectedNotebook.id, fileId);
        }

        // Remove from local state
        setSelectedNotebook(prev => ({
          ...prev,
          files: (prev?.files || []).filter(f => (f.fileId || f.id) !== fileId),
        }));

        // Also remove from nano-metadata rows
        removeNanoMetadataRow(fileId);

        // Re-upload the file
        await handleUploadFile(selectedNotebook.id, originalFile);

        console.log(`✅ [Re-upload] Successfully re-uploaded: ${fileName}`);
        setError(null);
      } catch (err) {
        console.error('Failed to re-upload file:', err);
        const backendMsg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to upload file';
        setError(`Failed to upload "${fileName}": ${backendMsg}`);
      }
    } else {
      // Should be unreachable, but keep a safe fallback.
      setError(`Source file required for "${fileName}": please select the file again to upload.`);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE "ULTRA-RECOVER" RE-OCR PROTOCOL (Frontend Handler)
  // ═══════════════════════════════════════════════════════════════════════════
  // This handler implements the frontend part of the Ultra-Recover protocol:
  // 1. Instant UI transition to "Deep Scanning" state with animation
  // 2. Capture previous quality for comparison display
  // 3. Poll for progress updates (via SSE or polling)
  // 4. Auto-trigger summary regeneration when complete
  // ═══════════════════════════════════════════════════════════════════════════
  const handleReprocessFile = async (
    file,
    reason = 'Re-index: missing search index',
    reprocessOptions = {}
  ) => {
    if (!selectedNotebook?.id || !file) return;

    const fileId = file.fileId || file.id;
    const fileName = file.originalName || file.name;
    const previousHealth = file.health || 0;
    const previousStatus = file.status || 'processing';
    const previousIsReady = file.isReady === true;
    const previousLifecycle = file.lifecycleSnapshot || null;

    // 🔒 PRODUCT LAW: "Re-index" is NOT Ultra-Recover. It must be a fast cache re-stamp only.
    const reasonNorm = String(reason || '').toLowerCase();
    const isReindex =
      reasonNorm.includes('re-index') ||
      reasonNorm.includes('reindex') ||
      reasonNorm.includes('missing search index') ||
      reasonNorm.includes('index repair');
    if (isReindex) {
      // Instant UI: restoring (fast path)
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f =>
          (f.fileId || f.id) === fileId
            ? {
                ...f,
                status: 'restoring',
                processingStage: 'cache_restore',
                processingMessage: 'Restoring from cache (fast path)…',
                processingProgress: 80,
                isReady: false,
                userTrust: { ...(f.userTrust || {}), state: 'processing' },
              }
            : f
        ),
      }));

      try {
        await intelligenceStudioApi.reindexFile(selectedNotebook.id, fileId, { reason });
        // Refresh notebook
        const latestResponse = await intelligenceStudioApi.getNotebook(selectedNotebook.id);
        const latestNotebook = latestResponse?.data?.data || latestResponse?.data || latestResponse;
        setSelectedNotebook(latestNotebook);
        setNotebooks(prev => prev.map(n => (n.id === latestNotebook.id ? latestNotebook : n)));
      } catch (err) {
        console.error('Re-index failed:', err);
        // Restore minimal failure state
        setSelectedNotebook(prev => ({
          ...prev,
          files: (prev?.files || []).map(f =>
            (f.fileId || f.id) === fileId
              ? { ...f, status: 'error', error: err?.message || 'REINDEX_FAILED' }
              : f
          ),
        }));
      }
      return;
    }

    const isFastReOcr =
      reprocessOptions?.tryCascade === false ||
      (String(reason || '').toLowerCase().includes('re-ocr') &&
        String(reason || '').toLowerCase().includes('fast'));

    console.log(`\n🔥 [ULTRA-RECOVER] ════════════════════════════════════════════`);
    console.log(
      `🔥 [ULTRA-RECOVER] Initiating ${isFastReOcr ? 'Fast' : 'Full'} Recovery Protocol`
    );
    console.log(`   📄 File: ${fileName}`);
    console.log(`   🔑 File ID: ${fileId}`);
    console.log(`   📊 Previous Health: ${previousHealth}%`);
    console.log(`   📋 Reason: ${reason}`);

    const targetThresholdRaw = reprocessOptions?.qualityThreshold;
    const targetThreshold = Number.isFinite(Number(targetThresholdRaw))
      ? Number(targetThresholdRaw)
      : 70;
    const tryCascade =
      reprocessOptions?.tryCascade !== undefined ? Boolean(reprocessOptions.tryCascade) : true;
    const forceEngine = reprocessOptions?.forceEngine;
    const forceOcr =
      reprocessOptions?.forceOcr !== undefined
        ? Boolean(reprocessOptions.forceOcr)
        : previousHealth < 70;

    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 1: INSTANT UI TRANSITION - "Deep Scanning" state with animation
    // ═══════════════════════════════════════════════════════════════════════════
    console.log(
      `   ⚡ Transitioning UI to ${isFastReOcr ? 'Fast Re-OCR' : 'Deep Scanning'} mode...`
    );

    setSelectedNotebook(prev => ({
      ...prev,
      files: (prev?.files || []).map(f =>
        (f.fileId || f.id) === fileId
          ? {
              ...f,
              status: 'processing',
              processingStage: isFastReOcr ? 'fast_reocr' : 'deep_scanning',
              processingMessage: isFastReOcr
                ? '⚡ Re-OCR (fast path)…'
                : '🔥 Ultra-Recover: Deep Scanning…',
              processingProgress: 10,
              isReady: false,
              userTrust: { ...(f.userTrust || {}), state: 'processing' },
              // Clear old warnings
              health: null,
              qualityScore: null,
              ocrQualityTier: null,
              // Store previous values for comparison
              _previousHealth: previousHealth,
              _ultraRecoverStartedAt: new Date().toISOString(),
            }
          : f
      ),
    }));

    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 2: TRIGGER BACKEND RECOVERY (with enhanced options)
    // ═══════════════════════════════════════════════════════════════════════════
    try {
      console.log(`   🚀 Sending Ultra-Recover request to backend...`);

      const response = await intelligenceStudioApi.reprocessFile(selectedNotebook.id, fileId, {
        reason,
        tryCascade,
        qualityThreshold: targetThreshold,
        forceEngine,
        forceOcr,
      });

      console.log(`   ✅ Backend queued recovery job:`, response?.data?.data?.jobId || 'unknown');

      // ═══════════════════════════════════════════════════════════════════════════
      // STEP 3: POLL FOR COMPLETION (with progressive UI updates)
      // ═══════════════════════════════════════════════════════════════════════════
      const pollForCompletion = async (maxAttempts = 60, intervalMs = 2000) => {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await new Promise(resolve => setTimeout(resolve, intervalMs));

          // Fetch latest notebook state
          try {
            const latestResponse = await intelligenceStudioApi.getNotebook(selectedNotebook.id);
            const latestNotebook =
              latestResponse?.data?.data || latestResponse?.data || latestResponse;
            const latestFile = latestNotebook?.files?.find(f => (f.fileId || f.id) === fileId);

            if (latestFile) {
              const latestStatus = (latestFile.status || '').toLowerCase();

              // Update UI with progress
              setSelectedNotebook(prev => ({
                ...prev,
                files: (prev?.files || []).map(f =>
                  (f.fileId || f.id) === fileId
                    ? { ...latestFile, _previousHealth: previousHealth }
                    : f
                ),
              }));

              // ✅ ENTERPRISE: Treat ALL terminal states as completion (prevents infinite polling/spinners).
              // Intelligence Studio canonical terminal states include ready_with_warnings + blocked_low_quality + retry_ocr.
              const isTerminal = [
                'ready',
                'ready_with_warnings',
                'blocked',
                'blocked_low_quality',
                'retry_ocr',
                'failed',
                'failed_timeout',
                'error',
                'duplicate',
              ].includes(latestStatus);

              // Check if complete
              if (isTerminal) {
                console.log(`   🏁 Recovery complete! Status: ${latestStatus}`);

                // ═══════════════════════════════════════════════════════════════════════════
                // STEP 4: AUTO-REGENERATE - Show improvement and optionally trigger summary
                // ═══════════════════════════════════════════════════════════════════════════
                if (latestStatus === 'ready' || latestStatus === 'ready_with_warnings') {
                  const newHealth = latestFile.health || 0;
                  const improvement = newHealth - previousHealth;

                  console.log(
                    `   📈 Quality improved: ${previousHealth}% → ${newHealth}% (+${improvement}%)`
                  );

                  // Update notebook list
                  setNotebooks(prev =>
                    prev.map(n => (n.id === latestNotebook.id ? latestNotebook : n))
                  );
                  setError(null);

                  // Signal that file needs fresh analysis (via metadata flag)
                  // The chat component can pick this up and suggest regenerating
                  if (latestFile._needsFreshAnalysis || improvement > 10) {
                    console.log(`   🧠 File marked for fresh AI analysis`);
                  }
                } else if (['blocked', 'blocked_low_quality', 'retry_ocr'].includes(latestStatus)) {
                  // Terminal but action-required
                  setNotebooks(prev =>
                    prev.map(n => (n.id === latestNotebook.id ? latestNotebook : n))
                  );
                  setError(
                    `Action required for "${fileName}": ${latestFile.processingMessage || latestFile.error || 'OCR quality is too low. Retry OCR or upload a clearer scan.'}`
                  );
                } else {
                  setError(
                    `Recovery failed for "${fileName}". ${latestFile.error || 'Try re-uploading with a clearer scan.'}`
                  );
                }

                return latestFile;
              }
            }
          } catch (pollError) {
            console.warn(`   ⚠️ Poll attempt ${attempt + 1} failed: ${pollError.message}`);
          }
        }

        // Timeout - refresh anyway
        console.warn(`   ⚠️ Polling timed out, refreshing...`);
        await refreshNotebook();
        return null;
      };

      // Start polling (non-blocking)
      pollForCompletion().then(result => {
        if (result) {
          console.log(`\n✅ [ULTRA-RECOVER] Complete for ${fileName}`);
        }
      });
    } catch (err) {
      console.error('❌ [ULTRA-RECOVER] Failed:', err);
      const backendMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Recovery failed';
      setError(`Ultra-Recover failed for "${fileName}": ${backendMsg}`);

      // Restore to previous state
      setSelectedNotebook(prev => ({
        ...prev,
        files: (prev?.files || []).map(f =>
          (f.fileId || f.id) === fileId
            ? {
                ...f,
                status: previousStatus,
                processingProgress: 0,
                processingStage: null,
                processingMessage: null,
                health: previousHealth, // Restore previous health
                isReady: previousIsReady,
                lifecycleSnapshot: previousLifecycle || f.lifecycleSnapshot,
              }
            : f
        ),
      }));
    }
  };

  return {
    refreshNotebook,
    handleUploadFile,
    handleFileSelect,
    handleRemoveDuplicates,
    handleRetryFile,
    handleRemoveFile,
    handleDeleteFile,
    handleRenameFile,
    handleReuploadFile,
    handleReprocessFile,
  };
}
